import { generateId } from './contentUtils';
import { ensureReferenceListBlockExists } from './apaReferenceListUtils';
import { isKnownAppHost } from './assetUrls';

// Preset background artwork ships inside the app build (public/images) instead
// of being loaded from a folder uploaded separately to the web host — a preset
// tile went blank whenever an image had not been copied to the server. Keeping
// the files with the bundle means every preset deploys atomically with the app.
//
// PUBLIC_URL is the deployed app root ("/app" in production, "" in development
// and tests). Resolving against the current document keeps the value an
// absolute URL, which the PPTX and PDF exporters require in order to embed a
// background image.
const PRESET_IMAGE_DIR = `${process.env.PUBLIC_URL || ''}/images`;

const presetImageUrl = (fileName) => {
  const path = `${PRESET_IMAGE_DIR}/${encodeURIComponent(fileName)}`;
  if (typeof window === 'undefined' || !window.location) return path;
  try {
    return new URL(path, window.location.href).href;
  } catch {
    return path;
  }
};

/**
 * Standard background patterns and images for templates
 */
export const BACKGROUND_PRESETS = {
  // Solid colors
  white: {
    id: 'white',
    name: 'Clean White',
    type: 'color',
    value: '#ffffff',
    preview: '#ffffff'
  },
  lightGray: {
    id: 'lightGray',
    name: 'Light Gray',
    type: 'color',
    value: '#f8fafc',
    preview: '#f8fafc'
  },
  warmWhite: {
    id: 'warmWhite',
    name: 'Warm White',
    type: 'color',
    value: '#fffbf5',
    preview: '#fffbf5'
  },
  coolBlue: {
    id: 'coolBlue',
    name: 'Cool Blue',
    type: 'color',
    value: '#f0f9ff',
    preview: '#f0f9ff'
  },

  // Gradients
  subtleGradient: {
    id: 'subtleGradient',
    name: 'Subtle Gradient',
    type: 'gradient',
    value: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    preview: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
  },
  blueGradient: {
    id: 'blueGradient',
    name: 'Blue Gradient',
    type: 'gradient',
    value: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    preview: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
  },
  warmGradient: {
    id: 'warmGradient',
    name: 'Warm Gradient',
    type: 'gradient',
    value: 'linear-gradient(135deg, #fffbf5 0%, #fef3c7 50%, #fffbf5 100%)',
    preview: 'linear-gradient(135deg, #fffbf5 0%, #fef3c7 100%)'
  },
  purpleGradient: {
    id: 'purpleGradient',
    name: 'Purple Gradient',
    type: 'gradient',
    value: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #faf5ff 100%)',
    preview: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)'
  },
  greenGradient: {
    id: 'greenGradient',
    name: 'Green Gradient',
    type: 'gradient',
    value: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)',
    preview: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
  },

  // Patterns (CSS-based)
  dots: {
    id: 'dots',
    name: 'Subtle Dots',
    type: 'pattern',
    value: 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    backgroundColor: '#ffffff',
    preview: '#f1f5f9'
  },
  grid: {
    id: 'grid',
    name: 'Light Grid',
    type: 'pattern',
    value: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)',
    backgroundSize: '40px 40px',
    backgroundColor: '#ffffff',
    preview: '#f1f5f9'
  },
  diagonalLines: {
    id: 'diagonalLines',
    name: 'Diagonal Lines',
    type: 'pattern',
    value: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #f1f5f9 10px, #f1f5f9 11px)',
    backgroundColor: '#ffffff',
    preview: '#f8fafc'
  },
  waves: {
    id: 'waves',
    name: 'Soft Waves',
    type: 'pattern',
    value: 'radial-gradient(ellipse at 50% 0%, #e0f2fe 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, #ddd6fe 0%, transparent 50%)',
    backgroundColor: '#ffffff',
    preview: '#f0f9ff'
  },
  geometric: {
    id: 'geometric',
    name: 'Geometric',
    type: 'pattern',
    value: 'linear-gradient(30deg, #f8fafc 12%, transparent 12.5%, transparent 87%, #f8fafc 87.5%, #f8fafc), linear-gradient(150deg, #f8fafc 12%, transparent 12.5%, transparent 87%, #f8fafc 87.5%, #f8fafc), linear-gradient(30deg, #f8fafc 12%, transparent 12.5%, transparent 87%, #f8fafc 87.5%, #f8fafc), linear-gradient(150deg, #f8fafc 12%, transparent 12.5%, transparent 87%, #f8fafc 87.5%, #f8fafc), linear-gradient(60deg, #f1f5f977 25%, transparent 25.5%, transparent 75%, #f1f5f977 75%, #f1f5f977), linear-gradient(60deg, #f1f5f977 25%, transparent 25.5%, transparent 75%, #f1f5f977 75%, #f1f5f977)',
    backgroundSize: '40px 70px',
    backgroundPosition: '0 0, 0 0, 20px 35px, 20px 35px, 0 0, 20px 35px',
    backgroundColor: '#ffffff',
    preview: '#f1f5f9'
  },

  // Server-hosted background images (16:9 ratio)
  'background-001': {
    id: 'background-001',
    name: 'Aurora Blush 1',
    type: 'image',
    value: presetImageUrl('Background 001.png'),
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#e0f4ff', // Fallback color matching the image edges
    preview: presetImageUrl('Background 001.png'),
    category: 'image'
  },
  'background-002': {
    id: 'background-002',
    name: 'Sapphire Sweep 2',
    type: 'image',
    value: presetImageUrl('Background 002.png'),
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#f0f8ff', // Fallback color matching the image edges
    preview: presetImageUrl('Background 002.png'),
    category: 'image'
  },
  'background-003': {
    id: 'background-003',
    name: 'Aqua Horizon 3',
    type: 'image',
    value: presetImageUrl('Background 003.png'),
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#f5f9ff', // Fallback color matching the image edges
    preview: presetImageUrl('Background 003.png'),
    category: 'image'
  },
  'background-004': {
    id: 'background-004',
    name: 'Pearl Facets 4',
    type: 'image',
    value: presetImageUrl('Background 004.png'),
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#eef4ff', // Fallback color matching the image edges
    preview: presetImageUrl('Background 004.png'),
    category: 'image'
  },
  'title-card-001': {
    id: 'title-card-001',
    name: 'Ivory Fold Title 1',
    type: 'image',
    value: presetImageUrl('TitleCard 001.png'),
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#eef4ff', // Fallback color matching the image edges
    preview: presetImageUrl('TitleCard 001.png'),
    category: 'image'
  }
};

export const BACKGROUND_PRESET_ALIASES = {
  bgImage001: 'background-001',
  bgImage002: 'background-002',
  bgImage003: 'background-003',
  bgImage004: 'background-004',
  titleCard001: 'title-card-001'
};

const isImageUrl = (value) => typeof value === 'string' && /^(data:image\/|https?:\/\/)/i.test(value);

const normalizeUrlPath = (value) => {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return '';
  try {
    const parsed = new URL(value);
    return decodeURIComponent(parsed.pathname || '').toLowerCase();
  } catch {
    return '';
  }
};

const normalizeFileToken = (value) => value
  .toLowerCase()
  .replace(/\s+/g, '')
  .replace(/[^a-z0-9.]/g, '');

const getFileTokenFromUrl = (value) => {
  const path = normalizeUrlPath(value);
  if (!path) return '';
  const fileName = path.split('/').filter(Boolean).pop() || '';
  return normalizeFileToken(fileName);
};

const buildPresetImageTokenMap = () => {
  const map = new Map();
  Object.values(BACKGROUND_PRESETS).forEach((preset) => {
    if (preset?.type !== 'image' || typeof preset.value !== 'string') return;
    const token = getFileTokenFromUrl(preset.value);
    if (!token) return;
    map.set(token, preset.id);
  });
  return map;
};

const PRESET_IMAGE_TOKENS = buildPresetImageTokenMap();

// The file-name fallback exists so this app's own preset artwork is recognised
// wherever it is served from — the current domain, the legacy one, a relative
// path. It must not reach past those, because it matches on nothing but the
// file name: a picture the author uploaded to their own bucket and called
// "Background 004.png" was silently answered with the bundled preset of the
// same name, and their link was never stored at all.
const isAppOwnedAssetUrl = (value) => {
  try {
    return isKnownAppHost(new URL(value).hostname);
  } catch {
    return false;
  }
};

const findPresetIdByImageUrl = (value) => {
  if (!isImageUrl(value)) return '';
  // An exact match on the preset's own address is safe from any host.
  const direct = Object.values(BACKGROUND_PRESETS).find((entry) => entry.type === 'image' && (entry.value === value || entry.preview === value));
  if (direct?.id) return direct.id;
  if (!isAppOwnedAssetUrl(value)) return '';
  const token = getFileTokenFromUrl(value);
  return PRESET_IMAGE_TOKENS.get(token) || '';
};

export const normalizeBackgroundPersistenceValue = (value, { allowEmpty = true } = {}) => {
  if (value === null || value === undefined) return allowEmpty ? '' : null;

  const candidate = typeof value === 'string'
    ? value.trim()
    : (value && typeof value === 'object' && typeof value.value === 'string' ? value.value.trim() : '');

  if (!candidate) return allowEmpty ? '' : null;
  if (/^blob:/i.test(candidate)) return allowEmpty ? '' : null;

  const aliasResolved = BACKGROUND_PRESET_ALIASES[candidate] || candidate;
  if (BACKGROUND_PRESETS[aliasResolved]) {
    return aliasResolved;
  }

  const matchedPresetId = findPresetIdByImageUrl(candidate);
  if (matchedPresetId) return matchedPresetId;

  if (isImageUrl(candidate)) {
    return candidate;
  }

  return candidate;
};

export const normalizeBackgroundMapForPersistence = (value) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [slideId, bgValue]) => {
    if (!slideId) return acc;
    const normalized = normalizeBackgroundPersistenceValue(bgValue);
    if (normalized) {
      acc[slideId] = normalized;
    }
    return acc;
  }, {});
};

export const resolveBackgroundPreset = (preset) => {
  if (!preset) return null;

  if (typeof preset === 'string') {
    const resolvedKey = normalizeBackgroundPersistenceValue(preset, { allowEmpty: false }) || preset;
    const existing = BACKGROUND_PRESETS[resolvedKey];
    if (existing) return existing;
    if (isImageUrl(preset)) {
      const matchedPresetId = findPresetIdByImageUrl(preset);
      const matchedPreset = matchedPresetId ? BACKGROUND_PRESETS[matchedPresetId] : null;
      if (matchedPreset) return matchedPreset;
      return {
        id: 'custom-image',
        name: 'Custom Image',
        type: 'image',
        value: preset,
        preview: preset,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return null;
  }

  if (preset.type === 'image' && typeof preset.value === 'string') {
    return {
      ...preset,
      preview: preset.preview || preset.value,
      backgroundSize: preset.backgroundSize || 'cover',
      backgroundPosition: preset.backgroundPosition || 'center center',
      backgroundRepeat: preset.backgroundRepeat || 'no-repeat'
    };
  }

  return preset;
};

export const getImageBackgroundPresetEntries = () => Object.entries(BACKGROUND_PRESETS)
  .filter(([_, preset]) => preset.type === 'image')
  .map(([key, preset]) => ({ key, ...preset }));

/**
 * Get CSS styles for a background preset
 */
export const getBackgroundStyles = (preset) => {
  if (!preset) return { backgroundColor: '#ffffff' };

  const bg = resolveBackgroundPreset(preset);
  if (!bg) return { backgroundColor: '#ffffff' };

  const styles = {};

  if (bg.type === 'color') {
    styles.backgroundColor = bg.value;
  } else if (bg.type === 'gradient') {
    styles.background = bg.value;
  } else if (bg.type === 'pattern') {
    styles.backgroundImage = bg.value;
    if (bg.backgroundColor) styles.backgroundColor = bg.backgroundColor;
    if (bg.backgroundSize) styles.backgroundSize = bg.backgroundSize;
    if (bg.backgroundPosition) styles.backgroundPosition = bg.backgroundPosition;
  } else if (bg.type === 'image') {
    styles.backgroundImage = `url(${bg.value})`;
    styles.backgroundSize = bg.backgroundSize || 'cover';
    styles.backgroundPosition = bg.backgroundPosition || 'center center';
    styles.backgroundRepeat = bg.backgroundRepeat || 'no-repeat';
    // Add fallback background color for when content extends beyond image
    if (bg.backgroundColor) {
      styles.backgroundColor = bg.backgroundColor;
    }
  }

  return styles;
};

/**
 * Create a minimal/blank template for quick start
 */
export const createEmptyTemplate = (options = {}) => {
  const template = {
  id: 'empty',
  name: 'Empty Deck',
  description: 'Start from a completely empty slide deck with no sections or slides',
  thumbnail: 'empty',
  category: 'basic',
  headerColor: '#64748b',
  bodyBackground: 'white',
  titleCardSettings: { enabled: true, title: 'Title Slide', subtitle: '' },
  endTitleCardSettings: { enabled: false },
  slideNames: {
    'section-1-slide-0': 'slide 1'
  },
  slideVisibility: {
    'section-1-slide-0': true
  },
  sections: [
    {
      id: 'section-1',
      title: 'Section 1',
      type: 'content',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>slide 1</h2>'
        }
      ]
    }
  ]
  };
  return options.includeReferenceList
    ? { ...template, sections: ensureReferenceListBlockExists(template.sections) }
    : template;
};

/**
 * Create a minimal/blank template for quick start
 */
export const createMinimalTemplate = (options = {}) => {
  const template = {
  id: 'minimal',
  name: 'Minimal Blank',
  description: 'A clean blank template with minimal content - perfect for building from scratch',
  thumbnail: 'minimal',
  category: 'basic',
  headerColor: '#64748b',
  bodyBackground: 'white',
  sections: [
    {
      id: 'bridge-in',
      title: 'Bridge-In',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'headline',
          content: '<h1 style="font-size: 2.5rem; text-align: center; margin: 2rem 0;">Lesson Title</h1>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p style="text-align: center; color: #64748b;">Add your introduction here...</p>'
        }
      ]
    },
    {
      id: 'outcomes',
      title: 'Learning Outcomes',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Learning Outcomes</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>By the end of this lesson, students will be able to:</p><ul><li>Outcome 1</li><li>Outcome 2</li><li>Outcome 3</li></ul>'
        }
      ]
    },
    {
      id: 'pre-assessment',
      title: 'Pre-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Pre-Assessment</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Add pre-assessment activities here...</p>'
        }
      ]
    },
    {
      id: 'participatory-learning',
      title: 'Participatory Learning',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Main Content</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Add your main lesson content here...</p>'
        }
      ]
    },
    {
      id: 'post-assessment',
      title: 'Post-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Post-Assessment</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Add post-assessment activities here...</p>'
        }
      ]
    },
    {
      id: 'summary',
      title: 'Summary & Next Steps',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Summary</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Key takeaways from this lesson...</p>'
        }
      ]
    },
    {
      id: 'resources',
      title: 'Resources & Materials',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Additional Resources</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Add resource links and materials here...</p>'
        }
      ]
    }
  ]
  };
  return options.includeReferenceList ? { ...template, sections: ensureReferenceListBlockExists(template.sections) } : template;
};

/**
 * Create Modern Professional template - sleek corporate style
 */
export const createModernProfessionalTemplate = (options = {}) => {
  const template = {
  id: 'modern-professional',
  name: 'Modern Professional',
  description: 'A sleek, corporate-style template ideal for professional development and business training',
  thumbnail: 'modern',
  category: 'professional',
  headerColor: '#1e40af',
  bodyBackground: 'subtleGradient',
  sections: [
    {
      id: 'bridge-in',
      title: 'Bridge-In',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'headline',
          content: '<h1 style="font-size: 3rem; text-align: center; margin: 2rem 0; color: #1e3a8a;">Professional Development Session</h1>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p style="font-size: 1.25rem; text-align: center; color: #475569;">Building Excellence Through Continuous Learning</p>'
        },
        {
          id: generateId(),
          type: 'divider',
          content: { style: 'solid', thickness: 2, color: '#1e40af', spacing: 'medium', width: '50%' }
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: '',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Today\'s Focus',
                  content: '<p>We will explore key strategies and frameworks that drive professional excellence in today\'s dynamic workplace environment.</p>',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#1e40af',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Expected Outcomes',
                  content: '<p>Gain actionable insights and practical tools that you can immediately apply to enhance your professional performance.</p>',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#16a34a',
                  showBorder: true
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'outcomes',
      title: 'Learning Outcomes',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Session Objectives</h2>'
        },
        {
          id: generateId(),
          type: 'stacked-cards',
          items: [
            {
              id: generateId(),
              title: 'Analyze',
              content: '<p>Critically evaluate current practices and identify areas for improvement in your professional domain.</p>',
              sideText: 'OBJECTIVE 1',
              backgroundColor: '#eff6ff',
              textColor: '#1e3a8a',
              borderColor: '#1e40af',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Implement',
              content: '<p>Apply proven frameworks and methodologies to real-world professional challenges.</p>',
              sideText: 'OBJECTIVE 2',
              backgroundColor: '#f0fdf4',
              textColor: '#14532d',
              borderColor: '#16a34a',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Lead',
              content: '<p>Develop leadership strategies that inspire teams and drive organizational success.</p>',
              sideText: 'OBJECTIVE 3',
              backgroundColor: '#fef3c7',
              textColor: '#78350f',
              borderColor: '#d97706',
              showBorder: true
            }
          ]
        }
      ]
    },
    {
      id: 'pre-assessment',
      title: 'Pre-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Current State Assessment</h2>'
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'info',
          content: '<p><strong>Reflection Exercise:</strong> Take a moment to consider your current professional challenges. What specific skills or knowledge gaps are you looking to address today?</p>'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '2x2',
          style: 'info',
          items: [
            {
              id: generateId(),
              title: 'Current Strengths',
              content: '<p>What professional competencies do you excel at?</p>'
            },
            {
              id: generateId(),
              title: 'Growth Areas',
              content: '<p>Where do you see the most opportunity for development?</p>'
            },
            {
              id: generateId(),
              title: 'Immediate Goals',
              content: '<p>What do you want to achieve in the next 30 days?</p>'
            },
            {
              id: generateId(),
              title: 'Long-term Vision',
              content: '<p>Where do you see yourself professionally in 2 years?</p>'
            }
          ]
        }
      ]
    },
    {
      id: 'participatory-learning',
      title: 'Participatory Learning',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Core Content: Strategic Frameworks</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Framework Overview',
              width: '40%',
              widthPercent: 40,
              cards: [
                {
                  id: generateId(),
                  title: 'The Professional Excellence Model',
                  content: '<p>A comprehensive approach that integrates:</p><ul><li>Strategic thinking</li><li>Effective communication</li><li>Continuous improvement</li><li>Stakeholder management</li></ul>',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#1e40af',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Implementation Steps',
              width: '60%',
              widthPercent: 60,
              cards: [
                {
                  id: generateId(),
                  title: 'Getting Started',
                  numberedList: [
                    { id: generateId(), text: 'Assess your current state and baseline metrics' },
                    { id: generateId(), text: 'Define clear, measurable objectives' },
                    { id: generateId(), text: 'Identify key stakeholders and resources' },
                    { id: generateId(), text: 'Develop an actionable implementation plan' },
                    { id: generateId(), text: 'Execute with regular progress reviews' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#f8fafc',
                  textColor: '#334155',
                  borderColor: '#94a3b8',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'exercise-box',
          style: 'exercise',
          content: '<p><strong>Group Activity:</strong> Break into small groups and discuss how you would apply this framework to a current project or challenge. Prepare a brief summary to share with the larger group.</p>'
        }
      ]
    },
    {
      id: 'post-assessment',
      title: 'Post-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Knowledge Check & Application</h2>'
        },
        {
          id: generateId(),
          type: 'worksheet',
          questions: [
            {
              id: generateId(),
              question: 'Summarize the key components of the framework discussed today. How do they interconnect?',
              answer: ''
            },
            {
              id: generateId(),
              question: 'Identify one specific challenge in your work that this framework could address. Outline your approach.',
              answer: ''
            },
            {
              id: generateId(),
              question: 'What metrics will you use to measure success in applying these concepts?',
              answer: ''
            }
          ]
        }
      ]
    },
    {
      id: 'summary',
      title: 'Summary & Next Steps',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Key Takeaways</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'What We Covered',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Today\'s Highlights',
                  numberedList: [
                    { id: generateId(), text: 'Professional excellence framework' },
                    { id: generateId(), text: 'Strategic implementation steps' },
                    { id: generateId(), text: 'Practical application exercises' },
                    { id: generateId(), text: 'Measurement and evaluation' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#1e40af',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Action Items',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Your Next Steps',
                  numberedList: [
                    { id: generateId(), text: 'Complete the reflection exercise' },
                    { id: generateId(), text: 'Draft your implementation plan' },
                    { id: generateId(), text: 'Schedule follow-up with your team' },
                    { id: generateId(), text: 'Review supplementary materials' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#16a34a',
                  showBorder: true
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'resources',
      title: 'Resources & Materials',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #1e3a8a;">Additional Resources</h2>'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '2x2',
          style: 'info',
          items: [
            {
              id: generateId(),
              title: 'Recommended Reading',
              content: '<p>Curated articles and books on professional development and leadership excellence.</p>'
            },
            {
              id: generateId(),
              title: 'Online Courses',
              content: '<p>Self-paced learning modules to deepen your understanding of key concepts.</p>'
            },
            {
              id: generateId(),
              title: 'Templates & Tools',
              content: '<p>Downloadable frameworks and planning templates for immediate application.</p>'
            },
            {
              id: generateId(),
              title: 'Support Network',
              content: '<p>Connect with peers and mentors for ongoing guidance and collaboration.</p>'
            }
          ]
        }
      ]
    }
  ]
  };
  return options.includeReferenceList ? { ...template, sections: ensureReferenceListBlockExists(template.sections) } : template;
};

/**
 * Create Creative Education template - colorful and engaging style
 */
export const createCreativeEducationTemplate = (options = {}) => {
  const template = {
  id: 'creative-education',
  name: 'Creative Education',
  description: 'A vibrant, engaging template perfect for K-12 education, workshops, and interactive learning',
  thumbnail: 'creative',
  category: 'education',
  headerColor: '#7c3aed',
  bodyBackground: 'purpleGradient',
  sections: [
    {
      id: 'bridge-in',
      title: 'Bridge-In',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'headline',
          content: '<h1 style="font-size: 3rem; text-align: center; margin: 2rem 0; color: #5b21b6;">🎨 Let\'s Learn Together! 🎨</h1>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p style="font-size: 1.25rem; text-align: center; color: #6b7280;">An exciting journey of discovery awaits!</p>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: '🤔 Think',
                  content: '<p style="text-align: center;">What do you already know about this topic?</p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: '💭 Wonder',
                  content: '<p style="text-align: center;">What questions do you have?</p>',
                  backgroundColor: '#ddd6fe',
                  textColor: '#4c1d95',
                  borderColor: '#8b5cf6',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: '🌟 Explore',
                  content: '<p style="text-align: center;">What do you want to discover?</p>',
                  backgroundColor: '#dcfce7',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'exercise-box',
          style: 'exercise',
          content: '<p><strong>🎯 Quick Start Activity:</strong> Turn to a partner and share one thing you\'re curious about today! You have 2 minutes.</p>'
        }
      ]
    },
    {
      id: 'outcomes',
      title: 'Learning Outcomes',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">🎯 What Will We Learn?</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>By the end of our adventure, you\'ll be able to:</p>'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '3x1',
          style: 'success',
          items: [
            {
              id: generateId(),
              title: '🔍 Discover',
              content: '<p>Find and explore new ideas and concepts</p>'
            },
            {
              id: generateId(),
              title: '🛠️ Create',
              content: '<p>Make something amazing with what you learn</p>'
            },
            {
              id: generateId(),
              title: '🗣️ Share',
              content: '<p>Teach others about your discoveries</p>'
            }
          ]
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'info',
          content: '<p><strong>Remember:</strong> There are no wrong answers when we\'re learning! Every question helps us grow. 🌱</p>'
        }
      ]
    },
    {
      id: 'pre-assessment',
      title: 'Pre-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">📋 Let\'s Check In!</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Before we dive in, let\'s see what you already know! Don\'t worry - this isn\'t a test, it\'s just to help us learn together.</p>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Rate Yourself',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'How confident do you feel?',
                  content: '<p>🌟 <strong>Super confident</strong> - I know a lot!</p><p>⭐ <strong>Pretty good</strong> - I know some things</p><p>✨ <strong>Just starting</strong> - This is new to me!</p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Quick Question',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Think About This...',
                  content: '<p style="font-style: italic;">If you could learn ONE amazing thing today, what would it be?</p><p style="color: #6b7280; font-size: 0.9rem;">Share with your neighbor! 🗣️</p>',
                  backgroundColor: '#ddd6fe',
                  textColor: '#4c1d95',
                  borderColor: '#8b5cf6',
                  showBorder: true
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'participatory-learning',
      title: 'Participatory Learning',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">🚀 Time to Explore!</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: '',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: '📚 Key Concept',
                  content: '<p>Here\'s the main idea we\'re exploring today. It\'s like the foundation of a building - everything else builds on top of it!</p>',
                  backgroundColor: '#ddd6fe',
                  textColor: '#4c1d95',
                  borderColor: '#8b5cf6',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: '🌈 Fun Fact!',
                  content: '<p>Did you know? Add an interesting fact here that will surprise and engage learners!</p>',
                  backgroundColor: '#fecaca',
                  textColor: '#7f1d1d',
                  borderColor: '#ef4444',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'heading',
          content: '<h3 style="color: #5b21b6;">🎮 Activity Time!</h3>'
        },
        {
          id: generateId(),
          type: 'stacked-cards',
          items: [
            {
              id: generateId(),
              title: 'Step 1: Watch & Listen',
              content: '<p>Pay attention as we explore the concept together. Ask questions anytime!</p>',
              sideText: '👀',
              backgroundColor: '#dcfce7',
              textColor: '#14532d',
              borderColor: '#22c55e',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 2: Try It Out',
              content: '<p>Now it\'s your turn! Practice what you\'ve learned with a hands-on activity.</p>',
              sideText: '✏️',
              backgroundColor: '#fef3c7',
              textColor: '#78350f',
              borderColor: '#f59e0b',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 3: Share & Discuss',
              content: '<p>Team up with classmates and share your discoveries. What did you learn?</p>',
              sideText: '🗣️',
              backgroundColor: '#ddd6fe',
              textColor: '#4c1d95',
              borderColor: '#8b5cf6',
              showBorder: true
            }
          ]
        },
        {
          id: generateId(),
          type: 'exercise-box',
          style: 'exercise',
          content: '<p><strong>🎨 Hands-On Challenge:</strong> Create something that shows what you\'ve learned! You could draw, write, build, or act it out. Be creative!</p>'
        }
      ]
    },
    {
      id: 'post-assessment',
      title: 'Post-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">🏆 Show What You Know!</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Let\'s celebrate your learning! Complete these activities to show how much you\'ve grown.</p>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Self-Check',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'I Can...',
                  isChecklist: true,
                  checklistItems: [
                    { id: generateId(), text: 'Explain the main concept in my own words', checked: false },
                    { id: generateId(), text: 'Give an example from real life', checked: false },
                    { id: generateId(), text: 'Teach this to someone else', checked: false },
                    { id: generateId(), text: 'Ask good questions about it', checked: false }
                  ],
                  checkmarkColor: '#22c55e',
                  backgroundColor: '#dcfce7',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Reflection',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: '💭 Think About It',
                  content: '<p><strong>3 things I learned:</strong></p><p><strong>2 things I found interesting:</strong></p><p><strong>1 question I still have:</strong></p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'summary',
      title: 'Summary & Next Steps',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">🎉 Amazing Work Today!</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p style="text-align: center; font-size: 1.25rem;">Let\'s celebrate what we accomplished together!</p>'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '2x2',
          style: 'success',
          items: [
            {
              id: generateId(),
              title: '🎯 We Explored',
              content: '<p>New ideas and exciting concepts</p>'
            },
            {
              id: generateId(),
              title: '🎨 We Created',
              content: '<p>Amazing projects and discoveries</p>'
            },
            {
              id: generateId(),
              title: '🤝 We Collaborated',
              content: '<p>Learned from each other</p>'
            },
            {
              id: generateId(),
              title: '🌟 We Grew',
              content: '<p>Became even more awesome!</p>'
            }
          ]
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'info',
          content: '<p><strong>🏠 Homework Challenge:</strong> Share what you learned today with someone at home. Teaching others helps you remember better!</p>'
        }
      ]
    },
    {
      id: 'resources',
      title: 'Resources & Materials',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2 style="color: #5b21b6;">📚 Keep Learning!</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Fun Extras',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: '🎬 Videos to Watch',
                  content: '<p>Exciting videos that explain more about our topic!</p>',
                  backgroundColor: '#fecaca',
                  textColor: '#7f1d1d',
                  borderColor: '#ef4444',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: '🎮 Games & Activities',
                  content: '<p>Practice what you learned with fun interactive games!</p>',
                  backgroundColor: '#dcfce7',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'More to Explore',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: '📖 Books & Stories',
                  content: '<p>Great reads to dive deeper into the topic!</p>',
                  backgroundColor: '#ddd6fe',
                  textColor: '#4c1d95',
                  borderColor: '#8b5cf6',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: '🔬 Projects to Try',
                  content: '<p>Hands-on experiments and projects for home!</p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            }
          ]
        }
      ]
    }
  ]
  };
  return options.includeReferenceList ? { ...template, sections: ensureReferenceListBlockExists(template.sections) } : template;
};

/**
 * Get all available preset templates
 */
/**
 * Create a PowerPoint-ready template: every slide is pre-broken with slide
 * dividers and holds only as much content as fits one exported 16:9
 * PowerPoint slide. Ships with the 16:9 export guides turned on so authors
 * can see the cut-off while they write.
 */
export const createPowerPointReadyTemplate = (options = {}) => {
  const slideBreak = () => ({
    id: generateId(),
    type: 'divider',
    content: {
      style: 'solid',
      thickness: 2,
      color: '#e5e7eb',
      spacing: 'medium',
      width: 'full',
      isSlideBreak: true
    }
  });

  // Frames position blocks on the designer stage in percent of the
  // content region below the header bar
  const HEADING_FRAME = { x: 6, y: 6, w: 88, h: 14 };
  const BODY_FRAME = { x: 6, y: 24, w: 88, h: 66 };

  const template = {
    id: 'pptx-ready',
    name: 'PowerPoint-Ready (16:9)',
    description: 'A fixed 16:9 canvas per slide: drag, scale, and position cards exactly as they will export to PowerPoint.',
    thumbnail: 'pptx',
    category: 'presentation',
    headerColor: '#c2410c',
    bodyBackground: 'white',
    slideDesignMode: 'canvas',
    titleCardSettings: { enabled: true, title: 'Lesson Title', subtitle: 'Course · Week · Date' },
    endTitleCardSettings: { enabled: true, title: 'Thank You', subtitle: 'Questions?' },
    sections: [
      {
        id: 'bridge-in',
        title: 'Bridge-In',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Hook: Why This Matters</h2>'
          },
          {
            id: generateId(),
            type: 'text',
            content: '<p>One engaging question, story, or scenario. Drag and resize this card anywhere on the 16:9 stage — the slide exports to PowerPoint exactly as you lay it out.</p>'
          }
        ]
      },
      {
        id: 'outcomes',
        title: 'Learning Outcomes',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>What You Will Learn</h2>'
          },
          {
            id: generateId(),
            type: 'list',
            content: '<ul><li>Outcome 1</li><li>Outcome 2</li><li>Outcome 3</li></ul>'
          }
        ]
      },
      {
        id: 'pre-assessment',
        title: 'Pre-Assessment',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>What Do You Already Know?</h2>'
          },
          {
            id: generateId(),
            type: 'list',
            content: '<ul><li>Quick poll or question 1</li><li>Question 2</li><li>Question 3</li></ul>'
          }
        ]
      },
      {
        id: 'participatory-learning',
        title: 'Participatory Learning',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Concept 1</h2>'
          },
          {
            id: generateId(),
            type: 'text',
            content: '<p>One concept per slide: a short explanation plus up to 4 bullets.</p><ul><li>Key point</li><li>Key point</li><li>Key point</li></ul>'
          },
          slideBreak(),
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Concept 2</h2>'
          },
          {
            id: generateId(),
            type: 'text',
            content: '<p>Need more room? Add another slide with the divider block (slide break) — each 16:9 stage is exactly one PowerPoint slide.</p>'
          },
          slideBreak(),
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Activity</h2>'
          },
          {
            id: generateId(),
            type: 'exercise-box',
            content: '<h4>Try It</h4><p>One activity prompt with 2–3 short instructions.</p>'
          }
        ]
      },
      {
        id: 'post-assessment',
        title: 'Post-Assessment',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Check Your Understanding</h2>'
          },
          {
            id: generateId(),
            type: 'list',
            content: '<ul><li>Review question 1</li><li>Review question 2</li><li>Review question 3</li></ul>'
          }
        ]
      },
      {
        id: 'summary',
        title: 'Summary & Next Steps',
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Key Takeaways</h2>'
          },
          {
            id: generateId(),
            type: 'list',
            content: '<ul><li>Takeaway 1</li><li>Takeaway 2</li><li>Next: reading / assignment</li></ul>'
          }
        ]
      }
    ]
  };

  // Give every block a designer frame: headings across the top, body below
  template.sections.forEach((section) => {
    (section.blocks || []).forEach((block) => {
      if (block.type === 'divider') return;
      block.frame = block.type === 'heading' ? { ...HEADING_FRAME } : { ...BODY_FRAME };
    });
  });

  return options.includeReferenceList
    ? { ...template, sections: ensureReferenceListBlockExists(template.sections) }
    : template;
};

/**
 * The BOPPPS sections a blank PowerPoint deck can be scaffolded with, in
 * teaching order. The selection screen turns these on and off individually;
 * with none selected the blank deck stays a single empty slide.
 */
export const BOPPPS_SECTION_OPTIONS = [
  { id: 'bridge-in', title: 'Bridge-In', description: 'Hook that connects to prior learning' },
  { id: 'outcomes', title: 'Learning Outcomes', description: 'What students will be able to do' },
  { id: 'pre-assessment', title: 'Pre-Assessment', description: 'Check what students already know' },
  { id: 'participatory-learning', title: 'Participatory Learning', description: 'The main teaching and activity slides' },
  { id: 'post-assessment', title: 'Post-Assessment', description: 'Check the outcomes were met' },
  { id: 'summary', title: 'Summary', description: 'Key takeaways and next steps' }
];

/**
 * Create a blank PowerPoint deck: a single empty 16:9 canvas slide with the
 * designer turned on, so authors can drag, scale, and position cards on an
 * otherwise empty stage and build the deck slide by slide. Mirrors the
 * PowerPoint-Ready template's canvas geometry.
 *
 * BOPPPS scaffolding is opt-in: pass `bopppsSections` with the section ids to
 * include and the deck starts with one empty titled slide per section instead
 * of the single unnamed slide.
 */
export const createBlankPowerPointTemplate = (options = {}) => {
  const HEADING_FRAME = { x: 6, y: 6, w: 88, h: 14 };

  const requested = Array.isArray(options.bopppsSections) ? options.bopppsSections : [];
  const bopppsSections = BOPPPS_SECTION_OPTIONS.filter((section) => requested.includes(section.id));

  if (bopppsSections.length > 0) {
    const template = {
      id: 'pptx-blank',
      name: 'PowerPoint Compatible (16:9)',
      description: 'An empty 16:9 slide deck scaffolded with the BOPPPS sections you selected.',
      thumbnail: 'pptx-blank',
      category: 'presentation',
      headerColor: '#c2410c',
      bodyBackground: 'white',
      slideDesignMode: 'canvas',
      titleCardSettings: { enabled: true, title: 'Lesson Title', subtitle: 'Course · Week · Date' },
      endTitleCardSettings: { enabled: false },
      slideNames: {},
      slideVisibility: {},
      sections: bopppsSections.map((section) => ({
        id: section.id,
        title: section.title,
        type: 'boppps',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: `<h2>${section.title}</h2>`,
            frame: { ...HEADING_FRAME }
          }
        ]
      }))
    };

    bopppsSections.forEach((section) => {
      const slideId = `${section.id}-slide-0`;
      template.slideNames[slideId] = section.title;
      template.slideVisibility[slideId] = true;
    });

    return options.includeReferenceList
      ? { ...template, sections: ensureReferenceListBlockExists(template.sections) }
      : template;
  }

  const template = {
    id: 'pptx-blank',
    name: 'PowerPoint Compatible (16:9)',
    description: 'An empty 16:9 slide deck — start from a single blank canvas slide and build your presentation from scratch.',
    thumbnail: 'pptx-blank',
    category: 'presentation',
    headerColor: '#c2410c',
    bodyBackground: 'white',
    slideDesignMode: 'canvas',
    titleCardSettings: { enabled: true, title: 'Lesson Title', subtitle: 'Course · Week · Date' },
    endTitleCardSettings: { enabled: false },
    slideNames: {
      'section-1-slide-0': 'Slide 1'
    },
    slideVisibility: {
      'section-1-slide-0': true
    },
    sections: [
      {
        id: 'section-1',
        title: 'Slide 1',
        type: 'content',
        blocks: [
          {
            id: generateId(),
            type: 'heading',
            content: '<h2>Slide Title</h2>',
            frame: { ...HEADING_FRAME }
          }
        ]
      }
    ]
  };

  return options.includeReferenceList
    ? { ...template, sections: ensureReferenceListBlockExists(template.sections) }
    : template;
};

export const getPresetTemplates = () => [
  {
    id: 'empty',
    name: 'Empty Deck',
    description: 'Start from a completely empty slide deck with no sections or slides',
    thumbnail: 'empty',
    category: 'basic',
    headerColor: '#64748b',
    bodyBackground: 'white',
    create: createEmptyTemplate
  },
  {
    id: 'minimal',
    name: 'Minimal Blank',
    description: 'A clean blank template with minimal content - perfect for building from scratch',
    thumbnail: 'minimal',
    category: 'basic',
    headerColor: '#64748b',
    bodyBackground: 'white',
    create: createMinimalTemplate
  },
  {
    id: 'pptx-ready',
    name: 'PowerPoint-Ready (16:9)',
    description: 'A fixed 16:9 canvas per slide: drag, scale, and position cards exactly as they will export to PowerPoint.',
    thumbnail: 'pptx',
    category: 'presentation',
    headerColor: '#c2410c',
    bodyBackground: 'white',
    create: createPowerPointReadyTemplate
  },
  {
    id: 'pptx-blank',
    name: 'PowerPoint',
    subtitle: 'Compatible',
    description: 'An empty 16:9 slide deck — start from a single blank canvas slide and build your presentation from scratch.',
    thumbnail: 'pptx-blank',
    category: 'presentation',
    headerColor: '#c2410c',
    bodyBackground: 'white',
    create: createBlankPowerPointTemplate
  },
  {
    id: 'modern-professional',
    name: 'Modern Professional',
    description: 'A sleek, corporate-style template ideal for professional development and business training',
    thumbnail: 'modern',
    category: 'professional',
    headerColor: '#1e40af',
    bodyBackground: 'subtleGradient',
    create: createModernProfessionalTemplate
  },
  {
    id: 'creative-education',
    name: 'Creative Education',
    description: 'A vibrant, engaging template perfect for K-12 education, workshops, and interactive learning',
    thumbnail: 'creative',
    category: 'education',
    headerColor: '#7c3aed',
    bodyBackground: 'purpleGradient',
    create: createCreativeEducationTemplate
  }
];

/**
 * Get a specific template by ID
 */
export const getPresetTemplateById = (id) => {
  const templates = getPresetTemplates();
  return templates.find(t => t.id === id);
};
